import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiClient } from '../../lib/api-client';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  Download,
  Star,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  TrendingUp,
  Award,
  Briefcase,
  DollarSign,
  Clock,
  Eye,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Users,
  CheckCircle,
  XCircle,
  BarChart3
} from 'lucide-react';

interface Candidate {
  _id: string;
  applicationId?: string;
  job_seeker_id?: string;
  user?: {
    _id: string;
    full_name: string;
    email: string;
    phone?: string;
    phone_number?: string;
    address?: string;
    skills?: string[];
    experience?: string;
  };
  job_id: string;
  cover_letter?: string;
  resume_url?: string;
  portfolio_url?: string;
  expected_salary?: number;
  availability?: string;
  status: string;
  applied_at?: string;
  created_at?: string;
  ai_score?: number;
  totalScore?: number;
  recommendation?: string;
  factors?: Array<{
    factor: string;
    score: number;
    details: string;
  }>;
}

interface Job {
  _id: string;
  id?: string;
  title: string;
  company: string;
  location?: string;
  job_type?: string;
  salary_range?: string;
  salary_min?: number;
  salary_max?: number;
  required_skills?: string[];
  requirements?: string[];
  experience_required?: string;
  experience_level?: string;
  status: string;
  category?: string;
  description?: string;
}

interface CvShortlistingProps {
  jobs: Job[];
  applications: any[];
}

export function CvShortlisting({ jobs, applications }: CvShortlistingProps) {
  const [selectedJob, setSelectedJob] = useState<string>('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [sortBy, setSortBy] = useState('ai_score');
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [analysisResults, setAnalysisResults] = useState<any>(null);

  // Helper to get job ID consistently
  const getJobId = (job: Job) => String(job._id || job.id || '');

  // Helper to check if a job is expired
  const isExpired = (job: Job) => {
    const expiryDate = (job as any).expiryDate || (job as any).expiry_date || (job as any).deadline;
    if (!expiryDate) return false;
    const d = new Date(expiryDate);
    if (isNaN(d.getTime())) return false;
    return d.getTime() <= Date.now();
  };

  // Compute application counts for each job (excluding expired jobs)
  const applicationCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const activeJobIds = new Set(
      jobs
        .filter(job => String(job.status || '').toLowerCase() === 'active' && !isExpired(job))
        .map(job => getJobId(job))
    );

    (applications || []).forEach((app: any) => {
      const jobId = String(app.job_id || app.jobId || '');
      // Only count applications for active, non-expired jobs
      if (jobId && activeJobIds.has(jobId)) {
        counts[jobId] = (counts[jobId] || 0) + 1;
      }
    });
    return counts;
  }, [applications, jobs]);

  // Filter to only show active jobs with applicants (excluding expired jobs)
  const activeJobsWithApplicants = useMemo(() => {
    return jobs.filter(job => {
      const jobId = getJobId(job);
      const isActive = String(job.status || '').toLowerCase() === 'active' && !isExpired(job);
      const hasApplicants = (applicationCounts[jobId] || 0) > 0;
      return isActive && hasApplicants;
    }).sort((a, b) => {
      // Sort by applicant count descending
      const countA = applicationCounts[getJobId(a)] || 0;
      const countB = applicationCounts[getJobId(b)] || 0;
      return countB - countA;
    });
  }, [jobs, applicationCounts]);

  // Also show active jobs without applicants (excluding expired jobs)
  const activeJobsWithoutApplicants = useMemo(() => {
    return jobs.filter(job => {
      const jobId = getJobId(job);
      const isActive = String(job.status || '').toLowerCase() === 'active' && !isExpired(job);
      const hasApplicants = (applicationCounts[jobId] || 0) > 0;
      return isActive && !hasApplicants;
    });
  }, [jobs, applicationCounts]);

  // Calculate total applications for active jobs only
  const totalActiveApplications = useMemo(() => {
    return Object.values(applicationCounts).reduce((sum, count) => sum + count, 0);
  }, [applicationCounts]);

  // Fetch candidates for selected job
  const fetchCandidates = async (jobId: string) => {
    if (!jobId) return;

    setLoading(true);
    try {
      // Use the correct endpoint with query parameter
      const data: any = await apiClient.request(`/admin/applications?job_id=${encodeURIComponent(jobId)}&limit=100`);
      const list = Array.isArray(data?.applications) ? data.applications : Array.isArray(data) ? data : [];

      // Normalize candidate data structure
      const normalizedCandidates = list.map((app: any) => ({
        _id: app._id,
        applicationId: app._id,
        job_seeker_id: app.job_seeker_id,
        job_id: app.job_id,
        user: app.user || {
          _id: app.job_seeker_id,
          full_name: app.full_name || app.name || '',
          email: app.email || '',
          phone: app.phone || app.phone_number || '',
          skills: app.skills || [],
          experience: app.experience || ''
        },
        cover_letter: app.cover_letter,
        resume_url: app.resume_url,
        portfolio_url: app.portfolio_url,
        expected_salary: app.expected_salary,
        availability: app.availability,
        status: app.status || 'pending',
        applied_at: app.applied_at || app.created_at,
        created_at: app.created_at || app.applied_at,
        ai_score: app.ai_score || app.totalScore,
        totalScore: app.totalScore || app.ai_score,
        factors: app.factors || []
      }));

      setCandidates(normalizedCandidates);
      setFilteredCandidates(normalizedCandidates);
      setAnalysisResults(null);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  // Run AI analysis on candidates
  const runAiAnalysis = async () => {
    if (!selectedJob) {
      toast.error('Please select a job first');
      return;
    }

    if (candidates.length === 0) {
      toast.error('No candidates to analyze for this job');
      return;
    }

    setAnalyzing(true);
    try {
      // Use the correct endpoint
      const data: any = await apiClient.request(`/admin/jobs/${selectedJob}/analyze-candidates`, { method: 'GET' });

      if (data && data.rankedCandidates) {
        setAnalysisResults(data);

        // Map analysis results back to candidates
        const rankedMap = new Map(
          data.rankedCandidates.map((r: any) => [String(r.applicationId || r.candidateId), r])
        );

        const updatedCandidates = candidates.map(candidate => {
          const analysis = rankedMap.get(String(candidate._id)) || rankedMap.get(String(candidate.job_seeker_id));
          if (analysis) {
            return {
              ...candidate,
              ai_score: (analysis as any).totalScore,
              totalScore: (analysis as any).totalScore,
              recommendation: (analysis as any).recommendation,
              factors: (analysis as any).factors
            };
          }
          return candidate;
        }).sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0));

        setCandidates(updatedCandidates);
        setFilteredCandidates(updatedCandidates);
        toast.success(`Analysis complete! ${data.rankedCandidates.length} candidates ranked.`);
      } else {
        toast.info('No analysis results returned');
      }
    } catch (error: any) {
      console.error('Error running AI analysis:', error);
      toast.error(error?.message || 'Failed to run AI analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  // Download single candidate CV
  const downloadCandidateCV = async (candidate: Candidate, format: 'pdf' | 'word' = 'pdf') => {
    try {
      const candidateId = candidate.job_seeker_id || candidate.user?._id;
      const applicationId = candidate._id || candidate.applicationId;

      if (!candidateId || !applicationId) {
        toast.error('Missing candidate or application ID');
        return;
      }

      // Use the correct backend endpoint
      const url = `/admin/candidates/${candidateId}/applications/${applicationId}/export?format=${format === 'word' ? 'docx' : format}`;

      const blob = await apiClient.requestBlob(url, { method: 'GET' });

      const candidateName = candidate.user?.full_name?.replace(/\s+/g, '_') || 'candidate';
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `CV_${candidateName}_${Date.now()}.${format === 'word' ? 'docx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`CV downloaded successfully`);
    } catch (error: any) {
      console.error('Error downloading CV:', error);
      toast.error(error?.message || 'Failed to download CV');
    }
  };

  // Export job candidates in bulk
  const exportCandidates = async (format: 'pdf' | 'word' | 'excel', type: 'single' | 'bulk' = 'bulk') => {
    try {
      if (!selectedJob) {
        toast.error('Please select a job first');
        return;
      }

      // For single candidate with the checkbox selected
      if (type === 'single' && selectedCandidates.length === 1) {
        const candidate = candidates.find(c => c._id === selectedCandidates[0]);
        if (candidate) {
          await downloadCandidateCV(candidate, format as 'pdf' | 'word');
          return;
        }
      }

      // For bulk export of all job candidates
      // Use the correct backend endpoint
      const url = `/admin/jobs/${selectedJob}/export-candidates?format=${format === 'word' ? 'xlsx' : format}&includeAIAnalysis=true`;

      const blob = await apiClient.requestBlob(url, { method: 'GET' });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `candidates_${selectedJob}_${Date.now()}.${format === 'excel' ? 'xlsx' : format === 'word' ? 'docx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Candidates exported successfully');
    } catch (error: any) {
      console.error('Error exporting candidates:', error);
      toast.error(error?.message || 'Export failed');
    }
  };

  // Download multiple selected CVs
  const downloadSelectedCVs = async (format: 'pdf' | 'word' = 'pdf') => {
    if (selectedCandidates.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }

    toast.info(`Downloading ${selectedCandidates.length} CV(s)...`);

    for (const candidateId of selectedCandidates) {
      const candidate = candidates.find(c => c._id === candidateId);
      if (candidate) {
        await downloadCandidateCV(candidate, format);
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  // Filter and sort candidates
  useEffect(() => {
    let filtered = [...candidates];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(candidate => {
        const userName = candidate.user?.full_name || '';
        const userEmail = candidate.user?.email || '';
        const skills = candidate.user?.skills?.join(' ') || '';
        return userName.toLowerCase().includes(searchLower) ||
          userEmail.toLowerCase().includes(searchLower) ||
          skills.toLowerCase().includes(searchLower);
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(candidate => candidate.status === statusFilter);
    }

    // Score filter
    if (scoreFilter !== 'all') {
      const scoreThreshold = parseInt(scoreFilter);
      filtered = filtered.filter(candidate =>
        (candidate.ai_score || candidate.totalScore || 0) >= scoreThreshold
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'ai_score':
          return (b.ai_score || b.totalScore || 0) - (a.ai_score || a.totalScore || 0);
        case 'name':
          return (a.user?.full_name || '').localeCompare(b.user?.full_name || '');
        case 'date':
          return new Date(b.applied_at || b.created_at || 0).getTime() - new Date(a.applied_at || a.created_at || 0).getTime();
        default:
          return 0;
      }
    });

    setFilteredCandidates(filtered);
  }, [candidates, searchTerm, statusFilter, scoreFilter, sortBy]);

  useEffect(() => {
    if (selectedJob) {
      fetchCandidates(selectedJob);
      setSelectedCandidates([]);
    }
  }, [selectedJob]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (score >= 40) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getRecommendationBadge = (recommendation: string) => {
    switch (recommendation) {
      case 'Highly Recommended':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />{recommendation}</Badge>;
      case 'Recommended':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200"><Star className="h-3 w-3 mr-1" />{recommendation}</Badge>;
      case 'Consider':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><Eye className="h-3 w-3 mr-1" />{recommendation}</Badge>;
      case 'Weak Match':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200"><AlertCircle className="h-3 w-3 mr-1" />{recommendation}</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-800 border-red-200"><XCircle className="h-3 w-3 mr-1" />{recommendation || 'Not Analyzed'}</Badge>;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved':
      case 'shortlisted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'reviewing': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const selectedJobDetails = jobs.find(j => getJobId(j) === selectedJob);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI-Powered CV Shortlisting</h1>
          <p className="text-gray-600">Automatically analyze and rank candidates based on job requirements</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 border-blue-200">
            <TrendingUp className="h-3 w-3 mr-1" />
            AI Powered
          </Badge>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600">Active Jobs</p>
                <p className="text-2xl font-bold text-blue-800">{activeJobsWithApplicants.length + activeJobsWithoutApplicants.length}</p>
              </div>
              <Briefcase className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-600">Jobs with Applicants</p>
                <p className="text-2xl font-bold text-green-800">{activeJobsWithApplicants.length}</p>
              </div>
              <Users className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-purple-600">Total Applications</p>
                <p className="text-2xl font-bold text-purple-800">{totalActiveApplications}</p>
              </div>
              <FileText className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-orange-600">Selected Candidates</p>
                <p className="text-2xl font-bold text-orange-800">{filteredCandidates.length}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Job Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-lg">
            <Briefcase className="h-5 w-5 mr-2 text-blue-600" />
            Select Job Position
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="flex-1 w-full">
              <Select value={selectedJob} onValueChange={setSelectedJob}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Choose a job with applicants to analyze" />
                </SelectTrigger>
                <SelectContent>
                  {activeJobsWithApplicants.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                        Active Jobs with Applicants
                      </div>
                      {activeJobsWithApplicants.map((job) => {
                        const count = applicationCounts[getJobId(job)] || 0;
                        return (
                          <SelectItem key={getJobId(job)} value={getJobId(job)}>
                            <div className="flex items-center justify-between w-full gap-3">
                              <span className="font-medium">{job.title}</span>
                              <span className="text-gray-500 text-sm">- {job.company}</span>
                              <Badge className="ml-auto bg-green-100 text-green-700 border-green-200">
                                <Users className="h-3 w-3 mr-1" />
                                {count} applicant{count !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </>
                  )}
                  {activeJobsWithApplicants.length === 0 && (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No active jobs with applicants found
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={runAiAnalysis}
              disabled={!selectedJob || analyzing || candidates.length === 0}
              className="h-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              {analyzing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing Candidates...
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Run AI Analysis
                </>
              )}
            </Button>
          </div>

          {/* Selected Job Details */}
          {selectedJobDetails && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Company:</span>
                  <span className="ml-2 font-medium">{selectedJobDetails.company}</span>
                </div>
                <div>
                  <span className="text-gray-500">Location:</span>
                  <span className="ml-2 font-medium">{selectedJobDetails.location || 'Not specified'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Type:</span>
                  <span className="ml-2 font-medium">{selectedJobDetails.job_type || 'Not specified'}</span>
                </div>
                {selectedJobDetails.experience_level && (
                  <div>
                    <span className="text-gray-500">Experience:</span>
                    <span className="ml-2 font-medium">{selectedJobDetails.experience_level}</span>
                  </div>
                )}
                {(selectedJobDetails.required_skills || selectedJobDetails.requirements)?.length > 0 && (
                  <div className="md:col-span-2">
                    <span className="text-gray-500">Required Skills:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(selectedJobDetails.required_skills || selectedJobDetails.requirements || []).slice(0, 5).map((skill: string, idx: number) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedJob && (
        <>
          {/* Analysis Results Summary */}
          {analysisResults && (
            <Card className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Award className="h-8 w-8 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Analysis Complete</h3>
                      <p className="text-sm text-gray-600">
                        {analysisResults.rankedCandidates?.length || 0} candidates analyzed and ranked
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-800">
                      {analysisResults.rankedCandidates?.filter((c: any) => c.totalScore >= 65).length || 0} Recommended
                    </Badge>
                    <Badge className="bg-yellow-100 text-yellow-800">
                      {analysisResults.rankedCandidates?.filter((c: any) => c.totalScore >= 35 && c.totalScore < 65).length || 0} Consider
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters and Controls */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search candidates by name, email, or skills..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="shortlisted">Shortlisted</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={scoreFilter} onValueChange={setScoreFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Scores</SelectItem>
                    <SelectItem value="80">80+ (Highly Recommended)</SelectItem>
                    <SelectItem value="65">65+ (Recommended)</SelectItem>
                    <SelectItem value="50">50+ (Consider)</SelectItem>
                    <SelectItem value="35">35+ (Weak Match)</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ai_score">AI Score</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="date">Application Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Export Controls */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-gray-600">
                  {selectedCandidates.length > 0 ? (
                    <span className="font-medium text-blue-600">{selectedCandidates.length} candidate(s) selected</span>
                  ) : (
                    `${filteredCandidates.length} candidate(s) found`
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportCandidates('excel', 'bulk')}
                    disabled={filteredCandidates.length === 0}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Excel Export
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Candidates List */}
          {loading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin mr-2 text-blue-600" />
                <span className="text-gray-600">Loading candidates...</span>
              </CardContent>
            </Card>
          ) : filteredCandidates.length === 0 ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No candidates found</h3>
                  <p className="text-gray-600 max-w-md">
                    {candidates.length === 0
                      ? 'There are no applications for this job yet.'
                      : 'Try adjusting your filters or run AI analysis to rank candidates.'}
                  </p>
                  {candidates.length > 0 && !analysisResults && (
                    <Button
                      onClick={runAiAnalysis}
                      className="mt-4 bg-blue-600 hover:bg-blue-700"
                      disabled={analyzing}
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Run AI Analysis
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredCandidates.map((candidate, index) => (
                <Card key={candidate._id} className="hover:shadow-md transition-all duration-200 border-l-4" style={{
                  borderLeftColor: candidate.totalScore
                    ? candidate.totalScore >= 80 ? '#22c55e'
                      : candidate.totalScore >= 60 ? '#eab308'
                        : candidate.totalScore >= 40 ? '#f97316'
                          : '#ef4444'
                    : '#e5e7eb'
                }}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-2 flex-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedCandidates.includes(candidate._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCandidates([...selectedCandidates, candidate._id]);
                              } else {
                                setSelectedCandidates(selectedCandidates.filter(id => id !== candidate._id));
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          {/* Rank badge */}
                          {candidate.totalScore && (
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${index < 3 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                              #{index + 1}
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <h3 className="text-base font-semibold text-gray-900">
                                {candidate.user?.full_name || 'Unknown Candidate'}
                              </h3>
                              <Badge className={`${getStatusColor(candidate.status)} text-xs px-2 py-0`}>
                                {candidate.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1">
                              {candidate.totalScore !== undefined && (
                                <Badge className={`${getScoreColor(candidate.totalScore)} border text-xs px-2 py-0.5`}>
                                  <Star className="h-3 w-3 mr-1" />
                                  {candidate.totalScore}%
                                </Badge>
                              )}
                              {candidate.recommendation && getRecommendationBadge(candidate.recommendation)}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                            <div className="space-y-1">
                              <div className="flex items-center text-xs text-gray-600">
                                <Mail className="h-3 w-3 mr-1.5 text-gray-400" />
                                {candidate.user?.email || 'No email'}
                              </div>
                              {(candidate.user?.phone || candidate.user?.phone_number) && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <Phone className="h-3 w-3 mr-1.5 text-gray-400" />
                                  {candidate.user?.phone || candidate.user?.phone_number}
                                </div>
                              )}
                              {candidate.user?.address && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <MapPin className="h-3 w-3 mr-1.5 text-gray-400" />
                                  {candidate.user?.address}
                                </div>
                              )}
                            </div>

                            <div className="space-y-1">
                              {candidate.expected_salary && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <DollarSign className="h-3 w-3 mr-1.5 text-gray-400" />
                                  Expected: Rs. {candidate.expected_salary.toLocaleString()}
                                </div>
                              )}
                              {candidate.availability && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <Clock className="h-3 w-3 mr-1.5 text-gray-400" />
                                  Available: {candidate.availability.replace(/_/g, ' ')}
                                </div>
                              )}
                              <div className="flex items-center text-xs text-gray-600">
                                <Calendar className="h-3 w-3 mr-1.5 text-gray-400" />
                                Applied: {new Date(candidate.applied_at || candidate.created_at || '').toLocaleDateString()}
                              </div>
                            </div>
                          </div>

                          {/* Skills */}
                          {candidate.user?.skills && candidate.user.skills.length > 0 && (
                            <div className="mb-2">
                              <h4 className="text-xs font-medium text-gray-700 mb-1">Skills:</h4>
                              <div className="flex flex-wrap gap-1">
                                {candidate.user.skills.slice(0, 8).map((skill: string, idx: number) => (
                                  <Badge key={idx} variant="secondary" className="text-[10px] px-1.5 py-0">
                                    {skill}
                                  </Badge>
                                ))}
                                {candidate.user.skills.length > 8 && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                    +{candidate.user.skills.length - 8} more
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}

                          {/* AI Analysis Breakdown */}
                          {candidate.factors && candidate.factors.length > 0 && (
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded p-2 mb-2 border border-blue-100">
                              <h4 className="text-xs font-semibold text-blue-900 mb-1.5 flex items-center">
                                <BarChart3 className="h-3 w-3 mr-1" />
                                AI Analysis Breakdown
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                {candidate.factors.map((factor, idx) => (
                                  <div key={idx} className="text-center">
                                    <div className="text-sm font-bold text-blue-700">{Math.round(factor.score)}%</div>
                                    <div className="text-[10px] text-gray-600">{factor.factor}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center space-x-1.5 pt-1.5 border-t">
                            {candidate.resume_url && (
                              <Button variant="outline" size="sm" asChild>
                                <a href={candidate.resume_url} target="_blank" rel="noopener noreferrer">
                                  <FileText className="h-4 w-4 mr-1" />
                                  View Resume
                                </a>
                              </Button>
                            )}
                            {candidate.portfolio_url && (
                              <Button variant="outline" size="sm" asChild>
                                <a href={candidate.portfolio_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-1" />
                                  Portfolio
                                </a>
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadCandidateCV(candidate, 'pdf')}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              PDF
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadCandidateCV(candidate, 'word')}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Word
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
